import React from 'react'
import { useLocation } from '@fs/zion-router'
import { useFeatureFlag } from '@fs/zion-flags'
import { usePermission } from '@fs/zion-permissions'
import { adminPages } from './components/admin/adminPages'

export default function useAdmin({ showHeader, showFooter, AdminRootComponent, FooterComponent }) {
  const location = useLocation()
  const isAdminRoute = location?.pathname?.startsWith('/admin')
  const adminHeaderFlag = useFeatureFlag('discovery_admin_header')
  const shouldCheckAdminPermissions = isAdminRoute && adminHeaderFlag?.isOn
  const [hasAssetAdminPermission, assetPermissionStatus] = usePermission(
    shouldCheckAdminPermissions ? 'DiscoveryAssetPermission' : null,
    'DiscoveryAdminAssetContext'
  )
  const [hasTimeMachinePermission, timeMachinePermissionStatus] = usePermission(
    shouldCheckAdminPermissions ? 'DiscoveryTimeMachineManageContentPermission' : null,
    'DiscoveryManageContentContext'
  )
  const [hasAdminUiPermission, adminUiPermissionStatus] = usePermission(
    shouldCheckAdminPermissions ? 'DiscoveryAdminUIPermission' : null,
    'DiscoveryAdminUIContext'
  )
  const [hasReadUserFeedbackPermission, feedbackPermissionStatus] = usePermission(
    shouldCheckAdminPermissions ? 'ReadUserFeedbackPermission' : null,
    'DiscoveryUserFeedbackContext'
  )
  const [hasContentReviewPermission, contentReviewPermissionStatus] = usePermission(
    shouldCheckAdminPermissions ? 'DiscoveryContentReviewPermission' : null,
    'DiscoveryManageContentContext'
  )
  const [hasDatabaseAdminPermission, databaseAdminPermissionStatus] = usePermission(
    shouldCheckAdminPermissions ? 'DiscoveryDatabasePermission' : null,
    'DiscoveryAdminDatabaseContext'
  )
  const permissionsResolved =
    assetPermissionStatus !== 'loading' &&
    timeMachinePermissionStatus !== 'loading' &&
    adminUiPermissionStatus !== 'loading' &&
    feedbackPermissionStatus !== 'loading' &&
    contentReviewPermissionStatus !== 'loading' &&
    databaseAdminPermissionStatus !== 'loading'

  // Wait for permissions before doing any routing logic.
  if (adminHeaderFlag?.isOn && isAdminRoute && !permissionsResolved) {
    return {
      shouldRenderAppRoot: false,
    }
  }
  if (
    !adminHeaderFlag?.isOn ||
    !isAdminRoute ||
    (hasAssetAdminPermission === false &&
      hasTimeMachinePermission === false &&
      hasAdminUiPermission === false &&
      hasReadUserFeedbackPermission === false &&
      hasContentReviewPermission === false &&
      hasDatabaseAdminPermission === false &&
      permissionsResolved)
  ) {
    return {
      shouldRenderAppRoot: true,
    }
  }

  const permissionByType = {
    contentReview: hasContentReviewPermission,
    databaseAdmin: hasDatabaseAdminPermission,
    adminUi: hasAdminUiPermission,
    asset: hasAssetAdminPermission,
    assetOrDatabase: Boolean(hasAssetAdminPermission || hasDatabaseAdminPermission),
  }
  const isPermitted = (subnavItem) => !subnavItem.permissionType || permissionByType[subnavItem.permissionType]

  const adminHeaderNav = [
    ...(hasAssetAdminPermission ? [{ title: 'Dashboard', to: '/admin' }] : []),
    ...adminPages
      .filter(
        ({ permissionType, permission }) =>
          (permissionType === 'asset' && hasAssetAdminPermission) ||
          (permissionType === 'manageContent' && hasAssetAdminPermission) ||
          (permissionType === 'timeMachineContent' && hasTimeMachinePermission) ||
          (permissionType === 'contentReview' && hasContentReviewPermission) ||
          (permissionType === 'databaseAdmin' && hasDatabaseAdminPermission) ||
          (permissionType === 'assetOrDatabase' && (hasAssetAdminPermission || hasDatabaseAdminPermission)) ||
          (permissionType === 'aiEfforts' &&
            (hasContentReviewPermission ||
              hasDatabaseAdminPermission ||
              hasAdminUiPermission ||
              hasAssetAdminPermission)) ||
          (permissionType === 'adminUi' &&
            permission === 'ReadUserFeedbackPermission' &&
            hasReadUserFeedbackPermission) ||
          (permissionType === 'adminUi' && permission !== 'ReadUserFeedbackPermission' && hasAdminUiPermission)
      )
      .map(({ title, path, subnav }) => {
        if (subnav?.length) {
          const permitted = subnav.filter(isPermitted)

          return {
            title,
            // Groups normally have a landing page of their own. The fallback covers one that does
            // not: send the heading to the first page this person can actually reach rather than
            // leaving it with nowhere to go.
            to: path ?? permitted[0]?.path,
            subnav: permitted.map(({ title: subnavTitle, path: subnavPath }) => ({
              title: subnavTitle,
              to: subnavPath,
            })),
          }
        }

        return { title, to: path }
      }),
  ]
  const adminRootProps = {
    header: {
      hidden: !showHeader,
      appName: 'Discovery Admin',
      nav: adminHeaderNav,
    },
    footerContent: showFooter ? <FooterComponent /> : undefined,
  }

  return {
    CurrentRoot: AdminRootComponent,
    rootProps: adminRootProps,
    shouldRenderAppRoot: true,
  }
}
